import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { EmptyState } from "@/components/shared/EmptyState";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { isUuidParam } from "@/utils/isUuidParam";
import {
  getPlatformUser,
  type PlatformUserAddress,
} from "@/services/platformUsersService";
import { practisesAnyProfession } from "@/lib/is-professional";

export interface UserDetailDialogProps {
  /** The person to show. When null the dialog cannot open. */
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Read-only admin view of a person, opened from the Solicitante row on the
 * request and RFQ detail screens (spec 218021de).
 *
 * DELIBERATELY HAS NO ACTIONS. Every write an admin might want here — disable
 * the account, reset 2FA, edit the profile — is a separate decision with its
 * own audit requirements, and a dialog that cannot mutate a user row cannot
 * damage one.
 *
 * The query is gated on `open` (control C-5). A person's document number and
 * home addresses should not be fetched every time an admin glances at a
 * request; they are fetched when an admin actually asks who this is.
 */
/**
 * A profession's human label for admin surfaces, by slug.
 *
 * Only the two incumbents have translations today; anything else falls back to
 * its slug. That is deliberate — a slug is legible and true, whereas hiding the
 * row (what this replaced) told the admin the person was not a professional.
 */
function professionLabel(
  profession: string | null | undefined,
  t: (key: string) => string,
): string {
  const slug = profession?.trim();
  if (!slug) return "";
  const known: Record<string, string> = {
    notary: "platform.quoteRequestsTriage.disciplineNotary",
    accountant: "platform.quoteRequestsTriage.disciplineAccountant",
  };
  const key = known[slug];
  return key ? t(key) : slug;
}

export function UserDetailDialog({
  userId,
  open,
  onOpenChange,
}: UserDetailDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["platform", "users", userId],
    queryFn: () => getPlatformUser(userId as string),
    // All three matter. `open` is the C-5 gate. The id check keeps the query
    // from firing with "null" in the URL on a request whose requester row was
    // deleted — and the UUID check keeps a malformed one from making a round
    // trip the server would only reject: the route runs it through
    // ParseUUIDPipe, so anything that is not a UUID is a guaranteed 400.
    enabled: open && isUuidParam(userId),
  });

  const fullName =
    [data?.firstName, data?.secondName, data?.lastName, data?.secondLastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    data?.displayName ||
    null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{fullName ?? t("platform.userDetail.title")}</DialogTitle>
          <DialogDescription>{t("platform.userDetail.description")}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : isError || !data ? (
          <EmptyState title={t("platform.userDetail.loadError")} className="py-6" />
        ) : (
          <div className="space-y-6">
            <DetailRows>
              <DetailRow label={t("platform.userDetail.email")}>{data.email}</DetailRow>
              {data.phone ? (
                <DetailRow label={t("platform.userDetail.phone")}>{data.phone}</DetailRow>
              ) : null}
              {data.documentNumber ? (
                <DetailRow label={t("platform.userDetail.document")}>
                  {data.documentType
                    ? `${t(`platform.userDetail.documentType_${data.documentType}`)} · ${data.documentNumber}`
                    : data.documentNumber}
                </DetailRow>
              ) : null}
              <DetailRow label={t("platform.userDetail.status")}>
                <StatusBadge variant={data.status === "active" ? "success" : "softDanger"}>
                  {t(`platform.userDetail.status_${data.status}`, { defaultValue: data.status })}
                </StatusBadge>
              </DetailRow>
              <DetailRow label={t("platform.userDetail.verification")}>
                {/* Two independent facts, shown together because an admin
                    reading them is asking one question: how much of this
                    person has been confirmed. */}
                {/* IDENTITY IS NOT ONE OF THESE ANY MORE. identityVerifiedAt
                    was stamped by the SKIP_KYC flag rather than by a check, so
                    this chip told an admin an identity had been confirmed when
                    none ever was. The provider is gone (task 9edda72); showing
                    the claim without the mechanism was the worse half. */}
                {[
                  data.emailVerifiedAt ? t("platform.userDetail.emailVerified") : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || t("platform.userDetail.nothingVerified")}
              </DetailRow>
              {practisesAnyProfession(data) ? (
                <DetailRow label={t("platform.userDetail.alsoProfessional")}>
                  {/*
                    NAMED FROM `profession`, not from the two booleans. This
                    showed nothing at all for a third profession — an admin
                    looking at a traductor saw no indication they were a
                    professional. Translated where a label exists, and falling
                    back to the slug rather than to blank, because a raw
                    "traductor_publico" tells the admin the truth while an empty
                    row tells them something false.
                  */}
                  {professionLabel(data.profession, t)}
                </DetailRow>
              ) : null}
            </DetailRows>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                {t("platform.userDetail.addressesTitle")}
              </h3>
              {data.addresses.length === 0 ? (
                <EmptyState title={t("platform.userDetail.noAddresses")} className="py-6" />
              ) : (
                <div className="space-y-2">
                  {data.addresses.map((address) => (
                    <SavedAddressCard key={address.id} address={address} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * One saved address. Extracted from the dialog body rather than left inline:
 * four nested conditionals inside a map inside a ternary is the shape that
 * makes a component hard to READ, and the review's cognitive-complexity check
 * flagged exactly that.
 */
function SavedAddressCard({ address }: { address: PlatformUserAddress }): JSX.Element {
  const { t } = useTranslation();
  const contact = [address.contactPersonName, address.contactPhone].filter(Boolean).join(" · ");
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-foreground">
          {address.label ?? t("platform.userDetail.addressUnlabelled")}
        </p>
        {address.isDefault ? (
          <StatusBadge variant="info">{t("platform.userDetail.addressDefault")}</StatusBadge>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {address.formattedAddress ?? [address.city, address.adminArea].filter(Boolean).join(", ")}
      </p>
      {contact ? <p className="mt-1 text-xs text-muted-foreground">{contact}</p> : null}
      {address.courierNotes ? (
        <p className="mt-1 text-xs text-muted-foreground">{address.courierNotes}</p>
      ) : null}
    </div>
  );
}
